/**
 * agentActivityFeed — the spine's per-section live "what is Asthra doing" feed.
 *
 * The run SSE stream now carries every AsthraAgent / tool-handler event
 * verbatim (agent lifecycle, LLM rounds, tool calls + results, retrieval
 * cascade steps, superintendent + librarian steps), each with the emitting
 * agent's span identity (``section_response.depth`` etc.). This module turns
 * one event into one human line and keeps a bounded, shell-owned ring buffer
 * per section — separate from ``SpineSection.subline`` so it survives the
 * ``section_state_changed`` subline wipe and spine rebuilds (same pattern as
 * ``liveDocLoadTracker``).
 *
 * Pure: no React, no I/O. Labels come from the tool display SSOT
 * (``config/toolDisplayNames.ts``) so the feed, the chat rail and Action
 * History never drift apart.
 */

import {
  buildLiveStatusPhrase,
  buildLiveStatusPhraseFromQuery,
  describeToolCallFromQuery,
} from "../../config/toolDisplayNames";
import type { ProgressEvent } from "../../types";

export type AgentActivityKind = "tool" | "tool_done" | "retrieval" | "step" | "error";

export interface AgentActivityLine {
  /** Stable per-line key (monotonic). */
  id: string;
  /** Client wall-clock ms when the line was produced. */
  at: number;
  /** Nesting depth of the emitting agent (0 = the section's main agent). */
  depth: number;
  kind: AgentActivityKind;
  text: string;
}

export type AgentActivityMap = ReadonlyMap<string, readonly AgentActivityLine[]>;

/** Lines kept per section — the newest N; older ones scroll off. */
export const ACTIVITY_FEED_MAX = 6;

/** Agent/superintendent/librarian step events rendered from the server's ``message``. */
const STEP_EVENTS: ReadonlySet<string> = new Set([
  "agent_start",
  "agent_end",
  "round_start",
  "llm_call_start",
  "llm_call_end",
  "terminal_accepted",
  "task_start",
  "drafting_response",
  "lead_start",
  "lead_done",
  "plan_layer_done",
  "assemble_done",
  "superintendent_complete",
  "librarian_l1_pick",
  "librarian_tree_render",
  "librarian_l2_pick",
  // Regulatory-audit narration. The auditor emits these with a pre-formatted,
  // audit-branded ``message`` ("Searching the audit library: …", "pass 2 of
  // 12", "Loaded guidance for … (9 expectations)"), so the "Show steps" feed
  // renders the audit's step-by-step trace. (The raw agent tool events don't
  // reach here with the top-level tool_name the tool branch needs — the audit's
  // on_progress carries that in section_response — so these branded lines are
  // what surface the audit steps.)
  "audit_progress",
  "audit_guidelines_loaded",
  "audit_context_inferred",
  "audit_precedents_fetched",
]);

const ERROR_EVENTS: ReadonlySet<string> = new Set(["resolve_span_failed", "api_error"]);

/** A section's feed is over (cleared) on these. */
export const ACTIVITY_CLEAR_SECTION_EVENTS: ReadonlySet<string> = new Set([
  "section_ready",
  "regen_complete",
  "resolve_section_proposed",
  "resolve_section_complete",
  "resolve_section_failed",
  "resolve_section_skipped",
  "module_skipped",
  // Regulatory audit terminals — close the step feed the audit opened.
  "audit_complete",
  "audit_error",
]);

/** Every feed is over on these (run-level terminals). */
export const ACTIVITY_CLEAR_ALL_EVENTS: ReadonlySet<string> = new Set(["pipeline_complete", "error", "cancelled"]);

/** A section's feed restarts on these (a new pass on the same row) — including
 *  the start of a regulatory audit, so the audit's step feed opens fresh rather
 *  than trailing the prior drafting/regen lines. */
export const ACTIVITY_RESET_SECTION_EVENTS: ReadonlySet<string> = new Set([
  "module_start",
  "regen_started",
  "audit_started",
]);

let lineSeq = 0;

const RETRIEVAL_VERB: Record<string, string> = { ok: "found", partial: "partial", fail: "no match" };

/**
 * One event → one feed line, or null for events with no writer-facing line.
 * ``now`` is injectable for tests.
 */
export function describeAgentEvent(event: ProgressEvent, now: number = Date.now()): AgentActivityLine | null {
  const payload = (event.section_response ?? {}) as Record<string, unknown>;
  const depth = typeof payload.depth === "number" && Number.isFinite(payload.depth) ? payload.depth : 0;
  const type = event.event_type;
  let kind: AgentActivityKind;
  let text: string;

  if (type === "tool_call_start") {
    if (!event.tool_name) return null;
    kind = "tool";
    text = buildLiveStatusPhraseFromQuery(event.tool_name, event.tool_query);
  } else if (type === "tool_call_result") {
    if (!event.tool_name) return null;
    kind = "tool_done";
    text = describeToolCallFromQuery(event.tool_name, event.tool_query, true);
    if (payload.ok === false) text += " — failed";
  } else if (type === "retrieval_step") {
    kind = "retrieval";
    const tool = typeof payload.tool === "string" ? payload.tool : null;
    const hint =
      typeof payload.hint === "string" ? payload.hint : typeof payload.file_label === "string" ? payload.file_label : undefined;
    const surface = typeof payload.surface === "string" ? payload.surface : undefined;
    const status = typeof payload.status === "string" ? payload.status : "";
    if (tool) {
      // Same SSOT mapping the chat rail uses for retrieval_step.
      const phrase = buildLiveStatusPhrase(tool, hint ? { query: hint } : undefined, surface);
      const verb = RETRIEVAL_VERB[status];
      text = verb ? `${phrase} — ${verb}` : phrase;
    } else {
      text = event.message || "retrieving…";
    }
    return { id: `${type}-${lineSeq++}`, at: now, depth: depth + 1, kind, text };
  } else if (STEP_EVENTS.has(type)) {
    kind = "step";
    text = event.message || type.replace(/_/g, " ");
  } else if (ERROR_EVENTS.has(type)) {
    kind = "error";
    text = event.message || "failed";
  } else {
    return null;
  }
  return { id: `${type}-${lineSeq++}`, at: now, depth, kind, text };
}

/** Append a line to a section's feed, keeping the newest ``max``. Returns a new map. */
export function pushActivity(
  map: AgentActivityMap,
  sectionId: string,
  line: AgentActivityLine,
  max: number = ACTIVITY_FEED_MAX,
): AgentActivityMap {
  const next = new Map(map);
  const prev = map.get(sectionId) ?? [];
  const lines = [...prev, line];
  next.set(sectionId, lines.length > max ? lines.slice(lines.length - max) : lines);
  return next;
}

/** Drop a section's feed. Returns the same map when nothing changed. */
export function clearActivity(map: AgentActivityMap, sectionId: string): AgentActivityMap {
  if (!map.has(sectionId)) return map;
  const next = new Map(map);
  next.delete(sectionId);
  return next;
}

export function clearAllActivity(map: AgentActivityMap): AgentActivityMap {
  return map.size === 0 ? map : new Map();
}

/**
 * Fold one SSE event into the feed map: resets on a new pass for the row,
 * clears on the row's terminal, clears everything on run terminals, otherwise
 * appends the described line. Returns the same map instance when unchanged so
 * React state setters can skip a render.
 */
export function applyActivityEvent(map: AgentActivityMap, event: ProgressEvent, now: number = Date.now()): AgentActivityMap {
  const type = event.event_type;
  if (ACTIVITY_CLEAR_ALL_EVENTS.has(type)) return clearAllActivity(map);
  const sectionId = event.module_id;
  if (!sectionId) return map;
  if (ACTIVITY_RESET_SECTION_EVENTS.has(type)) return clearActivity(map, sectionId);
  if (ACTIVITY_CLEAR_SECTION_EVENTS.has(type)) return clearActivity(map, sectionId);
  const line = describeAgentEvent(event, now);
  if (!line) return map;
  return pushActivity(map, sectionId, line);
}

/** The most recent line of a section's feed, if any. */
export function latestActivity(map: AgentActivityMap | undefined, sectionId: string): AgentActivityLine | null {
  const lines = map?.get(sectionId);
  return lines && lines.length > 0 ? lines[lines.length - 1] : null;
}

/** True when any section has a live feed (keeps the spine's elapsed tick running). */
export function hasAnyActivity(map: AgentActivityMap | undefined): boolean {
  return !!map && map.size > 0;
}
